"use client"

import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import { cn } from "@lib/utils"
import { SearchIcon } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import PlaceholderImage from "@modules/common/icons/placeholder-image"
import { resolveImageUrl } from "@/lib/util/image-url"

type Category = {
  id: number
  name: string
  description: string
  handle: string
  mpath: string
  isActive: number
  isInternal: number
  category_rank: number
  parent_category_id: number | null
  logo_image: string
  background_image: string
  character_image: string
  created_at: string
  updated_at: string
  deleted_at: string | null
}

const stripHtml = (content: string) => content?.replace(/<[^>]*>/g, "").trim() || ""

export default function AllCategoriesTemplate({
  categories,
}: {
  categories: Category[]
}) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")

  const filteredCategories = useMemo(() => {
    return categories.filter((category) =>
      category.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [categories, searchQuery])

  return (
    <div className="content-container py-6">
      <div className="mb-8 flex flex-col items-start text-2xl-semi md:flex-row md:items-center md:justify-between">
        <h1>{t("home.allCategories")}</h1>

        <div className="mt-4 flex h-full w-full items-center justify-end md:mt-0 md:w-auto">
          <div className="flex w-full flex-row">
            <Input
              className={cn(
                "w-full rounded-l-lg rounded-r-none border-2 border-r-0 border-muted-accent border-l border-y-2 focus-visible:outline-none focus-visible:ring-0 dark:bg-bg-body md:w-[350px]"
              )}
              placeholder={t("search.placeholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button className="h-full rounded-l-none border-2 border-muted-accent bg-muted-accent py-2.5 text-black hover:bg-button-primary hover:text-white dark:bg-[#111118] dark:text-white dark:hover:bg-button-primary">
              <SearchIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filteredCategories.map((category) => (
          <Card
            key={category.id}
            className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-orange-200 hover:shadow-xl"
          >
            <Link href={`/categories/${category.handle}`} className="block h-full">
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-50">
                {resolveImageUrl(category.background_image) ? (
                  <Image
                    src={resolveImageUrl(category.background_image)!}
                    alt={category.name}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <PlaceholderImage className="h-full w-full" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
              </div>

              <div className="p-4">
                <h3 className="line-clamp-2 text-lg font-bold text-slate-800 transition-colors duration-300 group-hover:text-orange-700">
                  {category.name}
                </h3>
                {stripHtml(category.description) && (
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">
                    {stripHtml(category.description)}
                  </p>
                )}
              </div>
            </Link>
          </Card>
        ))}
      </div>
    </div>
  )
}
